// ⚡️ FILE HANDLING: File operations for chat functionality
// Extracted from ChatPanel.js for better organization and reusability

import { useCallback } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import chatCache from '../../../utils/cache';
import { validateFile, logSecurityEvent } from '../utils/chatUtils';

// Custom hook for file handling operations
export const useFileHandling = () => {

  // Send file message
  const handleSendFileMessage = useCallback(async (url, type, {
    user,
    conversation,
    isAdminDashboard,
    firestoreConvId,
    dispatch,
    pendingMessages
  }) => {
    if (!user || !conversation) return;
    if (!url || !(type && (type.startsWith('image/') || type.startsWith('video/')))) return; // Prevent empty file messages
    
    // ⚡️ INSTANT UI UPDATE: Create optimistic file message for immediate display
    const optimisticFileMessage = {
      id: `temp_file_${Date.now()}_${Math.random()}`,
      from: user.email,
      type: type && type.startsWith('image/') ? 'image' : 'video',
      url,
      time: new Date().toLocaleTimeString(),
      status: 'sending',
      timestamp: new Date()
    };

    // ⚡️ INSTANT UI UPDATE: Add to pending messages immediately (no delay)
    dispatch({ type: 'ADD_PENDING_MESSAGE', payload: optimisticFileMessage });
    
    let messagesCol;
    if (conversation.id === 'admin' || isAdminDashboard) {
      const convId = firestoreConvId || (conversation.id === 'admin' ? `agent_${user.uid || user.id || user.email}` : conversation.id);
      if (!convId) return;
      messagesCol = collection(db, 'agentConversations', convId, 'messages');
    } else if (conversation.buyer && conversation.seller) {
      messagesCol = collection(db, 'sellerbuyerconversations', conversation.id, 'messages');
    } else {
      return;
    }
    
    // Security: Validate user data before saving file message
    if (!user || !user.email) {
      if (typeof window !== 'undefined' && window.toast) {
        window.toast.error('Authentication error. Please refresh and try again.');
      } else {
      }
      return;
    }
    
    const docRef = await addDoc(messagesCol, {
      from: user.email,
      fromUid: user.uid || user.email, // Fallback to email if uid is undefined
      type: type && type.startsWith('image/') ? 'image' : 'video',
      url,
      timestamp: serverTimestamp(),
      readBy: [user.email],
    });
    
    // Update optimistic message status to 'sent'
    dispatch({ 
      type: 'UPDATE_PENDING_MESSAGES', 
      payload: pendingMessages.map(msg => 
        msg.url === url && msg.status === 'sending' 
          ? { ...msg, status: 'sent', firestoreId: docRef.id }
          : msg
      )
    });
  }, []);

  // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced file upload with background processing
  const handleFileUpload = useCallback(async (file, {
    user,
    conversation,
    dispatch,
    chatCache,
    handleSendFileMessage
  }) => {
    if (!file || !user) return;
    
    // 🛡️ REMOVED CLIENT-SIDE RATE LIMITING - Now handled server-side via API
    
    // Security: Log file upload attempt
    logSecurityEvent('FILE_UPLOAD_ATTEMPT', { 
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      userId: user.uid
    }, 'info');
    
    // Security: Validate file using server-side rules
    const validation = validateFile(file);
    if (!validation.isValid) {
      logSecurityEvent('FILE_UPLOAD_REJECTED', { 
        fileName: file.name,
        reason: validation.error 
      }, 'warning');
      
      if (typeof window !== 'undefined' && window.toast) {
        window.toast.error(validation.error);
      } else {
        alert(validation.error);
      }
      return;
    }

    // ⚡️ PERFORMANCE OPTIMIZATION: Show upload preview immediately
    dispatch({ type: 'SET_UPLOADING_FILE', payload: {
      file,
      type: file.type,
      name: file.name,
      size: file.size,
      previewUrl: URL.createObjectURL(file)
    }});
    dispatch({ type: 'SET_UPLOADING', payload: true });
    dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: 0 });

    // 🛡️ SECURITY: Upload file via secure API endpoint with server-side validation
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('conversationId', conversation?.id || '');
      formData.append('type', file.type.startsWith('image/') ? 'image' : 'video');
      
      const response = await fetch('/api/chat/upload-file', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await user?.getIdToken()}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'File upload failed');
      }
      
      const result = await response.json();
          
      // ⚡️ PERFORMANCE OPTIMIZATION: Cache the media file
      const mediaId = `${Date.now()}_${file.name}`;
      chatCache.cacheMedia(mediaId, {
        url: result.fileUrl,
        type: file.type,
        name: file.name,
        size: file.size
      });
          
      // ⚡️ PERFORMANCE OPTIMIZATION: Send message after upload completes
      await handleSendFileMessage(result.fileUrl, file.type, {
        user,
        conversation,
        isAdminDashboard: false, // This will be passed from the component
        firestoreConvId: null, // This will be passed from the component
        dispatch,
        pendingMessages: [] // This will be passed from the component
      });
      dispatch({ type: 'SET_UPLOADING_FILE', payload: null });
      dispatch({ type: 'SET_UPLOADING', payload: false });
      dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: 0 });
      
    } catch (error) {
      dispatch({ type: 'SET_UPLOADING_FILE', payload: null });
      dispatch({ type: 'SET_UPLOADING', payload: false });
      dispatch({ type: 'SET_UPLOAD_PROGRESS', payload: 0 });
      if (typeof window !== 'undefined' && window.toast) {
        window.toast.error('Upload failed. Please try again.');
      } else {
        alert('Upload failed. Please try again.');
      }
    }
  }, []);

  // Drag & Drop handlers
  const handleDragOver = useCallback((e, dispatch) => {
    e.preventDefault();
    dispatch({ type: 'SET_DRAG_OVER', payload: true });
  }, []);

  const handleDragLeave = useCallback((e, dispatch) => {
    e.preventDefault();
    dispatch({ type: 'SET_DRAG_OVER', payload: false });
  }, []);

  const handleDrop = useCallback(async (e, dispatch, handleFileUpload) => {
    e.preventDefault();
    dispatch({ type: 'SET_DRAG_OVER', payload: false });
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Handle the first file (you can extend this to handle multiple files)
      const file = files[0];
      await handleFileUpload(file);
    }
  }, []);

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoize image click handler
  const handleImageClick = useCallback((imageUrl, dispatch) => {
    dispatch({ type: 'SET_OPENED_IMAGE', payload: imageUrl });
  }, []);

  return {
    handleSendFileMessage,
    handleFileUpload,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleImageClick
  };
};
